"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PayPeriods = exports.Weekday = void 0;
const luxon_1 = require("luxon");
var Weekday;
(function (Weekday) {
    Weekday[Weekday["Monday"] = 1] = "Monday";
    Weekday[Weekday["Tuesday"] = 2] = "Tuesday";
    Weekday[Weekday["Wednesday"] = 3] = "Wednesday";
    Weekday[Weekday["Thursday"] = 4] = "Thursday";
    Weekday[Weekday["Friday"] = 5] = "Friday";
    Weekday[Weekday["Saturday"] = 6] = "Saturday";
    Weekday[Weekday["Sunday"] = 7] = "Sunday";
})(Weekday || (exports.Weekday = Weekday = {}));
/**
 * Utility class for managing pay period calculations and boundaries.
 * Handles the configuration and calculation of weekly pay periods which
 * aligns with Zeal payroll system reporting periods.
 */
class PayPeriods {
    /**
     * Sets the 7 day work week for a given pay period.
     *
     * Controls the reporting period for a paycheck in Zeal.
     * Reporting periods are used only for tracking purposes and do not affect
     * overtime calculations or pay dates. They are printed on the paystub as
     * the pay period. To reduce confusion, this should be aligned to the overtime
     * calculation window.
     *
     * Note: it does not matter if you add a shift outside of the reporting period
     * to a check. This is useful incase a shift is logged late and needs to be
     * added to a check with a more recent reporting period.
     */
    static payPeriodStartOn = Weekday.Sunday;
    static payPeriodEndOn = Weekday.Saturday;
    static payPeriodTimeZone = 'America/Chicago';
    static achDelayHours = 48;
    /**
     * The date of the paycheck. Checks need to be approved by 2 PM PST two days
     * ahead of the check date.
     */
    static payday = Weekday.Wednesday;
    static getLastPayPeriodCanonicalEnd() {
        const now = luxon_1.DateTime.local({ zone: PayPeriods.payPeriodTimeZone });
        const daysToSubtract = (now.weekday - PayPeriods.payPeriodEndOn + 7) % 7;
        // If today is the reporting period end day, we want the previous one
        const adjustedDays = daysToSubtract === 0 ? 7 : daysToSubtract;
        return new Date(now.minus({ days: adjustedDays }).toFormat('yyyy-MM-dd'));
    }
    static getCanonicalPayPeriodDates(date, timezone) {
        const boundary = PayPeriods.getPayPeriodBoundary(date, timezone);
        return {
            start: new Date(boundary.start.toFormat('yyyy-MM-dd')),
            end: new Date(boundary.end.toFormat('yyyy-MM-dd')),
        };
    }
    static getPayPeriodBoundary(date, timezone = PayPeriods.payPeriodTimeZone) {
        const reportDate = luxon_1.DateTime.fromJSDate(date, {
            zone: timezone,
        });
        // Calculate start of current pay period (Sunday)
        // Luxon uses 1-7 for Monday-Sunday, so Sunday = 7
        // We need to find the most recent Sunday (or today if it's Sunday)
        const daysBackToSunday = reportDate.weekday === 7 ? 0 : reportDate.weekday;
        const start = reportDate
            .minus({ days: daysBackToSunday })
            .startOf('day');
        // Calculate end of current pay period (Saturday)
        const end = start.plus({ days: 6 }).endOf('day');
        return { start, end };
    }
    static calculateShiftPayPeriods(shift, timezone = PayPeriods.payPeriodTimeZone) {
        const shiftStart = luxon_1.DateTime.fromJSDate(shift.startTime, {
            zone: timezone,
        });
        const shiftEnd = luxon_1.DateTime.fromJSDate(shift.endTime, {
            zone: timezone,
        });
        if (shiftStart >= shiftEnd) {
            throw new Error('Start time must be before end time');
        }
        const periods = [];
        let currentTime = shiftStart;
        while (currentTime < shiftEnd) {
            const { start: periodStart, end: periodEnd } = PayPeriods.getPayPeriodBoundary(currentTime.toJSDate(), timezone);
            const segmentStart = luxon_1.DateTime.max(currentTime, periodStart);
            const segmentEnd = luxon_1.DateTime.min(shiftEnd, periodEnd);
            if (segmentStart >= segmentEnd) {
                break;
            }
            const segmentMinutes = Math.round(segmentEnd.diff(segmentStart, 'minutes').minutes);
            if (segmentMinutes > 0) {
                periods.push({
                    startDate: periodStart.toJSDate(),
                    endDate: periodEnd.toJSDate(),
                });
            }
            currentTime = periodEnd.plus({ milliseconds: 1 });
        }
        if (periods.length === 0) {
            throw new Error('No valid pay periods found for shift');
        }
        return periods;
    }
    static daysUntilNextPayday(from) {
        return (PayPeriods.payday - from.weekday + 7) % 7;
    }
    /** First payday after a canonical (UTC-midnight) period end. */
    static nominalCheckDate(canonicalEnd) {
        const dayAfter = luxon_1.DateTime.fromJSDate(canonicalEnd, {
            zone: 'utc',
        }).plus({ days: 1 });
        return dayAfter.plus({
            days: PayPeriods.daysUntilNextPayday(dayAfter),
        });
    }
    /**
     * The pay period a nurse's paycheck preview should show: the most
     * recently ended period through its payday, then the week in progress.
     * Unlike determineCheckDate, checkDate here is the nominal payday and is
     * never pushed out by the ACH cutoff.
     */
    static getUpcomingPreviewPayPeriod(now = new Date()) {
        const zone = PayPeriods.payPeriodTimeZone;
        const nowZoned = luxon_1.DateTime.fromJSDate(now, { zone });
        const nominalCheckDate = PayPeriods.nominalCheckDate;
        const currentBoundary = PayPeriods.getPayPeriodBoundary(now);
        const ended = PayPeriods.getCanonicalPayPeriodDates(currentBoundary.start.minus({ days: 1 }).toJSDate());
        const paydayPassed = nowZoned.toFormat('yyyy-MM-dd') >
            nominalCheckDate(ended.end).toFormat('yyyy-MM-dd');
        const period = paydayPassed
            ? PayPeriods.getCanonicalPayPeriodDates(now)
            : ended;
        return {
            periodStart: period.start,
            periodEnd: period.end,
            checkDate: new Date(nominalCheckDate(period.end).toFormat('yyyy-MM-dd')),
        };
    }
    /**
     * Calculate check date as the first payday AFTER the pay period ends
     *
     * @param payPeriodEnd Date of the end of the pay period
     */
    static determineCheckDate(payPeriodEnd) {
        const start = luxon_1.DateTime.now().plus({ hours: PayPeriods.achDelayHours });
        const dayAfterPayPeriod = luxon_1.DateTime.fromJSDate(payPeriodEnd).plus({
            days: 1,
        });
        const daysToPayday = PayPeriods.daysUntilNextPayday(dayAfterPayPeriod);
        let checkDate = dayAfterPayPeriod.plus({ days: daysToPayday });
        // If check date is in the past, move to next available payday
        if (checkDate < start) {
            const daysUntilNextPayday = PayPeriods.daysUntilNextPayday(start);
            checkDate = start.plus({ days: daysUntilNextPayday });
        }
        return checkDate;
    }
    /**
     * Allocate minutes worked across pay periods based on shift timing.
     * This is a pure function that works only with data models and primitives.
     *
     * @param shift Shift data with id, startTime, and minutesWorked
     * @param payPeriods Array of pay periods to allocate minutes to
     * @returns Result containing updated pay periods and optional additional pay period
     */
    static allocateMinutesWorked(shift, payPeriods, timezone = PayPeriods.payPeriodTimeZone) {
        if ((!shift.minutesWorked || shift.minutesWorked <= 0) &&
            (!shift.onCallMinutesWorked || shift.onCallMinutesWorked <= 0)) {
            throw new Error('Shift must have minutes worked to allocate');
        }
        if (payPeriods.length === 0) {
            throw new Error('Shift must have pay periods to allocate minutes to');
        }
        const shiftStart = luxon_1.DateTime.fromJSDate(shift.startTime).setZone(timezone);
        let remainingMinutes = shift.minutesWorked ?? 0;
        let remainingOnCallMinutes = shift.onCallMinutesWorked ?? 0;
        let currentTime = shiftStart;
        const updatedPayPeriods = [];
        // Sort pay periods by start date
        const sortedPayPeriods = [...payPeriods].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
        for (const payPeriod of sortedPayPeriods) {
            if (remainingMinutes <= 0 && remainingOnCallMinutes <= 0) {
                updatedPayPeriods.push({
                    ...payPeriod,
                    minutesWorked: 0,
                    onCallMinutesWorked: 0,
                });
                continue;
            }
            const periodStart = luxon_1.DateTime.fromJSDate(payPeriod.startDate).setZone(timezone);
            const periodEnd = luxon_1.DateTime.fromJSDate(payPeriod.endDate).setZone(timezone);
            const workStartTime = luxon_1.DateTime.max(currentTime, periodStart);
            if (workStartTime >= periodEnd) {
                updatedPayPeriods.push({
                    ...payPeriod,
                    minutesWorked: 0,
                    onCallMinutesWorked: 0,
                });
                continue;
            }
            const timeUntilPeriodEnd = periodEnd.diff(workStartTime, 'minutes').minutes;
            const minutesForThisPeriod = Math.min(remainingMinutes, Math.round(timeUntilPeriodEnd));
            const onCallMinutesForThisPeriod = Math.min(remainingOnCallMinutes, Math.round(timeUntilPeriodEnd));
            updatedPayPeriods.push({
                ...payPeriod,
                minutesWorked: minutesForThisPeriod,
                onCallMinutesWorked: onCallMinutesForThisPeriod,
            });
            remainingMinutes -= minutesForThisPeriod;
            remainingOnCallMinutes -= onCallMinutesForThisPeriod;
            const totalMinutesForThisPeriod = minutesForThisPeriod + onCallMinutesForThisPeriod;
            currentTime = workStartTime.plus({
                minutes: totalMinutesForThisPeriod,
            });
        }
        let additionalPayPeriod;
        // If there are remaining minutes, create an additional pay period
        if (remainingMinutes > 0 || remainingOnCallMinutes > 0) {
            const { start: nextPeriodStart, end: nextPeriodEnd } = PayPeriods.getPayPeriodBoundary(currentTime.toJSDate(), timezone);
            additionalPayPeriod = {
                startDate: nextPeriodStart.toJSDate(),
                endDate: nextPeriodEnd.toJSDate(),
                minutesWorked: remainingMinutes,
                onCallMinutesWorked: remainingOnCallMinutes,
            };
        }
        return {
            payPeriods: updatedPayPeriods,
            additionalPayPeriod,
        };
    }
}
exports.PayPeriods = PayPeriods;
//# sourceMappingURL=pay-periods.js.map